import React, { useEffect, useState } from "react";

import { useGetCropMixComposerScoreFactors, useLocation, useNavigate, useTranslation } from "utils";

import { CMC_STEP_4_TOGGLE_OPTIONS, GOAL_LEVEL_2 } from "models";

import { Flex, Icon, KlimRadioGroup, PageSpin } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { CropsOverviewTable } from "pages/app/crop-mix-composer/components/compositionStep/CropsOverviewTable";
import { ScoreSidePanel } from "pages/app/crop-mix-composer/components/compositionStep/ScoreSidePanel";
import { CropMixPieChart } from "pages/app/crop-mix-composer/components/overviewStep/CropMixPieChart";
import { CropMixSummaryTable } from "pages/app/crop-mix-composer/components/overviewStep/CropMixSummaryTable";
import { calculateAggregateMatchScore, generateGoalScores } from "pages/app/crop-mix-composer/utils/cropMixComposer";

export type GoalToScoreMap = Record<GOAL_LEVEL_2, number>;

export const MixOverview = () => {
  const { t } = useTranslation("cropMixComposer");
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedCrops, isPageLoading, activeCropMixId, selectedGoals, setAggregateGoalScores, setAggregateMatchScore, weightShareDistribution } =
    useCropMixComposerContext();

  const { data: scoreFactors } = useGetCropMixComposerScoreFactors();

  const [selectedToggleOption, setSelectedToggleOption] = useState<CMC_STEP_4_TOGGLE_OPTIONS>(CMC_STEP_4_TOGGLE_OPTIONS.SEED_SHARE);

  useEffect(() => {
    if (!scoreFactors) return;

    const aggregateGoalScores = generateGoalScores(selectedGoals, weightShareDistribution, scoreFactors, selectedCrops);
    setAggregateGoalScores(aggregateGoalScores);
    const aggregateMatchScore = calculateAggregateMatchScore(selectedCrops, weightShareDistribution);
    setAggregateMatchScore(aggregateMatchScore);
  }, [scoreFactors, selectedCrops, selectedGoals, setAggregateGoalScores, setAggregateMatchScore, weightShareDistribution]);

  if (isPageLoading) return <PageSpin />;

  const toggleOptions = [
    { label: t(`page.${CMC_ROUTES.OVERVIEW}.pieChartToggle.seedShare`), value: CMC_STEP_4_TOGGLE_OPTIONS.SEED_SHARE },
    { label: t(`page.${CMC_ROUTES.OVERVIEW}.pieChartToggle.weightShare`), value: CMC_STEP_4_TOGGLE_OPTIONS.WEIGHT_SHARE },
  ];

  const basePath = location.pathname.split("cmc")[0];

  return (
    <Flex className="crop-mix-composer-overlay-step-mix-details" data-testid="crop-mix-composer-overlay-mix-details">
      <Flex className="mix-details-section" vertical gap="2rem">
        <Flex className="mix-details-section-header margin-top-100" align="center" justify="space-between">
          <p className="header-title paragraph-m-bold margin-0">{t(`page.${CMC_ROUTES.OVERVIEW}.mixOverview`)}</p>
          <Flex className="header-actions" align="flex-end" gap="0.5rem">
            <Icon
              className="edit-icon"
              iconName="tabler/edit"
              onClick={() => {
                navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.COMPOSITION}${location.search}`);
              }}
            />
          </Flex>
        </Flex>
        <Flex className="mix-details-section-mix-chart" vertical gap="1rem">
          <KlimRadioGroup
            type="other"
            size="middle"
            dataTestId="mix-details-result-toggle"
            className="mix-details-result-toggle"
            options={toggleOptions}
            onChange={(e) => setSelectedToggleOption(e.target.value)}
            value={selectedToggleOption}
            optionType="button"
            buttonStyle="outline"
          />
          <Flex className="mix-details-container" gap="2rem">
            <CropMixPieChart selectedToggleOption={selectedToggleOption} />
            <CropMixSummaryTable selectedCrops={selectedCrops} />
          </Flex>
        </Flex>
        <Flex className="mix-details-section-table" vertical>
          <p className="table-title paragraph-m-bold">{t(`page.${CMC_ROUTES.OVERVIEW}.mixDetails`)}</p>
          <CropsOverviewTable cropMixComposerResponse={selectedCrops} />
        </Flex>
      </Flex>
      <ScoreSidePanel />
    </Flex>
  );
};
